/*
 * muun Category page
 * ----------------------------------------------------------------------------
 * Used by sections/mu-category-grid.liquid and sections/mu-category-tabs.liquid
 * (template: collection.category).
 *
 * <mu-category-grid>
 *   - Intercepts the native color filter form (filter.v.option.* params) and
 *     re-renders the section through the Section Rendering API instead of a
 *     full page load. The URL is kept in sync (pushState), back/forward work.
 *   - Desktop swatch row: applies on every change. Mobile drawer (the theme's
 *     native <x-drawer>, see mu-category-color-filter.liquid): changes only
 *     apply when the drawer closes, by any means (Apply/Clear button, X,
 *     backdrop, Escape) — mirrors the site's own filter drawer. Until then
 *     its "N Ergebnisse anzeigen" button previews the count for the picks.
 *   - Toggles the "mehr…" swatch overflow. That UI state lives on the host
 *     element, so it survives re-renders.
 *   - Without JS the form is a plain GET form and still filters natively.
 *
 * <mu-category-tabs>
 *   - Centers the active tab. Tab links deliberately do not carry the active
 *     `filter.*` params, so switching category resets the color filter.
 */
(() => {
  if (window.muCategoryLoaded) return;
  window.muCategoryLoaded = true;

  const FILTER_PREFIX = 'filter.';
  const FILTERS_CHANGED_EVENT = 'mu-category:filters-changed';
  const CARRY_LINK_SELECTOR = '[data-mu-category-carry-filters]';

  /* Returns `href` with its filter params replaced by the ones in `search`. */
  const withFilters = (href, search) => {
    const url = new URL(href, window.location.origin);
    const currentKeys = new Set([...url.searchParams.keys()].filter((key) => key.startsWith(FILTER_PREFIX)));
    currentKeys.forEach((key) => url.searchParams.delete(key));

    new URLSearchParams(search).forEach((value, key) => {
      if (key.startsWith(FILTER_PREFIX)) url.searchParams.append(key, value);
    });

    return url.pathname + url.search + url.hash;
  };

  const syncCarryLinks = (root) => {
    root.querySelectorAll(CARRY_LINK_SELECTOR).forEach((link) => {
      if (!link.dataset.baseHref) link.dataset.baseHref = link.getAttribute('href');
      link.setAttribute('href', withFilters(link.dataset.baseHref, window.location.search));
    });
  };

  class MuCategoryGrid extends HTMLElement {
    connectedCallback() {
      this.abortController = null;
      this.drawerPreview = null;
      this.handleChange = this.handleChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleFilterDrawerHide = this.handleFilterDrawerHide.bind(this);
      this.handleFilterDrawerClick = this.handleFilterDrawerClick.bind(this);
      this.handleFilterDrawerChange = this.handleFilterDrawerChange.bind(this);

      this.addEventListener('change', this.handleChange);
      this.addEventListener('submit', this.handleSubmit);
      this.addEventListener('click', this.handleClick);
      window.addEventListener('popstate', this.handlePopState);

      this.bindFilterDrawer();
      this.syncUiState();
      syncCarryLinks(this);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.handlePopState);
      this.unbindFilterDrawer();
      this.abortController?.abort();
      this.drawerPreview?.controller.abort();
    }

    handleChange(event) {
      // The drawer's swatches (mobile) are deliberately not handled here — they
      // only apply when the drawer closes, see handleFilterDrawerHide.
      if (event.target.closest('[data-mu-category-filter-form]')) this.submitFilters();
    }

    handleSubmit(event) {
      if (!event.target.matches('[data-mu-category-filter-form]')) return;
      event.preventDefault();
      this.submitFilters();
    }

    handleClick(event) {
      if (event.target.closest('[data-mu-category-more]')) {
        this.toggleUiState('swatchesExpanded');
        return;
      }

      const link = event.target.closest('[data-mu-category-link]');
      if (link && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        this.load(link.getAttribute('href'));
      }
    }

    handlePopState() {
      this.load(window.location.href, { updateHistory: false });
    }

    toggleUiState(key) {
      if (key in this.dataset) {
        delete this.dataset[key];
      } else {
        this.dataset[key] = '';
      }
      this.syncUiState();
    }

    syncUiState() {
      this.querySelectorAll('[data-mu-category-more]').forEach((button) => {
        button.setAttribute('aria-expanded', String('swatchesExpanded' in this.dataset));
      });
    }

    // Re-attach after every innerHTML swap (load()), since that destroys the previous drawer element.
    // Listeners are bound directly on the drawer itself (not delegated from `this`): while open, the
    // native <x-drawer> moves to document.body, so it's no longer a descendant of <mu-category-grid>.
    bindFilterDrawer() {
      this.unbindFilterDrawer();
      this.filterDrawer = this.querySelector('[data-mu-category-filter-drawer]');
      this.filterDrawer?.addEventListener('dialog:after-hide', this.handleFilterDrawerHide);
      this.filterDrawer?.addEventListener('click', this.handleFilterDrawerClick);
      this.filterDrawer?.addEventListener('change', this.handleFilterDrawerChange);
    }

    unbindFilterDrawer() {
      this.filterDrawer?.removeEventListener('dialog:after-hide', this.handleFilterDrawerHide);
      this.filterDrawer?.removeEventListener('click', this.handleFilterDrawerClick);
      this.filterDrawer?.removeEventListener('change', this.handleFilterDrawerChange);
    }

    // The mobile drawer's swatches apply once it closes, however it closes
    // (Apply/Clear button, X, backdrop, Escape) — same convention as the
    // site's own filter drawer (facets-drawer in sections/main-collection.liquid).
    handleFilterDrawerHide() {
      const form = this.filterDrawer?.querySelector('[data-mu-category-filter-drawer-form]');
      if (form) this.submitFilters(form);
    }

    handleFilterDrawerClick(event) {
      if (!event.target.closest('[data-mu-category-filter-clear]')) return;
      this.filterDrawer.querySelectorAll('.mu-category-filter__input').forEach((input) => {
        input.checked = false;
      });
      // Closing the drawer triggers handleFilterDrawerHide, which submits the now-empty form.
      event.target.dispatchEvent(new CustomEvent('dialog:force-close', { bubbles: true, composed: true }));
    }

    // The drawer's picks only apply once it closes, so its "N Ergebnisse anzeigen" button would keep
    // the count of the applied filters. Render the section for the picks and take the label from there:
    // count, translation and plural come from the same Liquid as the grid. load() reuses that request
    // when the drawer closes on the same picks, so previewing costs no extra request.
    handleFilterDrawerChange() {
      const form = this.filterDrawer?.querySelector('[data-mu-category-filter-drawer-form]');
      if (!form) return;

      this.drawerPreview?.controller.abort();
      const href = this.filtersHref(form);
      const controller = new AbortController();
      const preview = { href, controller, request: this.fetchGrid(href, controller.signal) };
      this.drawerPreview = preview;

      preview.request
        .then((nextGrid) => {
          if (this.drawerPreview !== preview) return;
          const label = nextGrid.querySelector('[data-mu-category-filter-apply]')?.textContent;
          const button = this.filterDrawer?.querySelector('[data-mu-category-filter-apply]');
          if (label && button) button.textContent = label;
        })
        // Keep the current label; closing the drawer still applies the picks (load() handles failures).
        .catch(() => {});
    }

    submitFilters(form = this.querySelector('[data-mu-category-filter-form]')) {
      if (!form) return;
      this.load(this.filtersHref(form));
    }

    /* The page URL (path + query) for the filters picked in `form`. */
    filtersHref(form) {
      const url = new URL(form.getAttribute('action'), window.location.origin);
      new URLSearchParams(new FormData(form)).forEach((value, key) => url.searchParams.append(key, value));

      // Keep an alternate template (?view=category) while it isn't assigned to the collection yet.
      const view = new URLSearchParams(window.location.search).get('view');
      if (view) url.searchParams.set('view', view);
      return url.pathname + url.search;
    }

    /* Renders this section for `href` through the Section Rendering API; resolves to its <mu-category-grid>. */
    async fetchGrid(href, signal) {
      const sectionUrl = new URL(href, window.location.origin);
      sectionUrl.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(sectionUrl, { signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = html.querySelector('mu-category-grid');
      if (!nextGrid) throw new Error('Section markup not found');
      return nextGrid;
    }

    async load(href, { updateHistory = true } = {}) {
      const url = new URL(href, window.location.origin);

      this.abortController?.abort();
      const abortController = new AbortController();
      this.abortController = abortController;
      this.setAttribute('aria-busy', 'true');

      // Reuse the drawer's count preview when it rendered exactly this state (see handleFilterDrawerChange).
      const preview = this.drawerPreview;
      this.drawerPreview = null;
      const previewRequest = preview?.href === href ? preview.request : null;
      if (preview && !previewRequest) preview.controller.abort();

      // Remember the focused swatch so keyboard users keep their place after the re-render.
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      const focusedSelector =
        focused?.name && focused?.value
          ? `[name="${CSS.escape(focused.name)}"][value="${CSS.escape(focused.value)}"]`
          : null;

      try {
        const nextGrid = await (previewRequest ?? this.fetchGrid(href, abortController.signal));
        // A reused preview request ignores abortController — drop it if a newer load() took over meanwhile.
        if (abortController.signal.aborted) return;

        this.innerHTML = nextGrid.innerHTML;
        if (updateHistory) window.history.pushState({}, '', url.pathname + url.search);

        this.bindFilterDrawer();
        this.syncUiState();
        syncCarryLinks(this);
        if (focusedSelector) this.querySelector(focusedSelector)?.focus();

        document.dispatchEvent(new CustomEvent(FILTERS_CHANGED_EVENT, { detail: { url: url.toString() } }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(url.toString());
      } finally {
        if (this.abortController === abortController) this.removeAttribute('aria-busy');
      }
    }
  }

  class MuCategoryTabs extends HTMLElement {
    connectedCallback() {
      this.centerActiveTab();
    }

    centerActiveTab() {
      const track = this.querySelector('.mu-category-tabs__track');
      const active = this.querySelector('[aria-current="page"]');
      if (!track || !active || track.scrollWidth <= track.clientWidth) return;
      track.scrollLeft = active.offsetLeft - (track.clientWidth - active.offsetWidth) / 2;
    }
  }

  if (!window.customElements.get('mu-category-grid')) {
    window.customElements.define('mu-category-grid', MuCategoryGrid);
  }
  if (!window.customElements.get('mu-category-tabs')) {
    window.customElements.define('mu-category-tabs', MuCategoryTabs);
  }
})();
